/* eslint-disable no-console */
import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { RouterModule } from '@angular/router';
import { format } from 'date-fns';
import { filter, map, Observable, Subscription, timer } from 'rxjs';

import { BaccaratTableComponent } from './baccarat-table/baccarat-table.component';
import { BurnCardsComponent } from './burn-cards/burn-cards.component';
import { ChatComponent } from './chat/chat.component';
import { ClosedTableComponent } from './closed-table/closed-table.component';
import { CountDownComponent } from './count-down/count-down.component';
import {
    BaccaratOutcome,
    BaccaratPhase,
    DealerAssistTableErrorCode,
    DealerConsoleAction,
    FlagStateType
} from './models/communication/baccarat-enums.model';
import { BaccaratEventType, DealerAssistTableErrorEvent } from './models/communication/baccarat-events.model';
import { BaccaratState } from './models/communication/baccarat-state.model';
import { PhaseTitleComponent } from './phase-title/phase-title.component';
import { RoundVoidedComponent } from './round-voided/round-voided.component';
import { DealerService } from './services/dealer.service';
import { ShoeChangeComponent } from './shoe-change/shoe-change.component';
import { WaitingForPlayersComponent } from './waiting-for-players/waiting-for-players.component';
import { environment } from '../environments/environment';

@Component({
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        BaccaratTableComponent,
        BurnCardsComponent,
        CountDownComponent,
        ClosedTableComponent,
        WaitingForPlayersComponent,
        ShoeChangeComponent,
        PhaseTitleComponent,
        MatSnackBarModule,
        MatButtonModule,
        ChatComponent,
        RoundVoidedComponent
    ],
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
    protected readonly BaccaratOutcome = BaccaratOutcome;
    protected readonly BaccaratPhase = BaccaratPhase;

    protected time$: Observable<string> = this.createTimeObservable();
    protected state$!: Observable<BaccaratState>;
    protected version: string = environment.version;

    private subscription?: Subscription;

    constructor(
        private readonly dealerService: DealerService,
        private readonly snackBar: MatSnackBar
    ) {}

    ngOnInit(): void {
        this.state$ = this.dealerService.getStateObservable().pipe(filter((state): state is BaccaratState => !!state));

        this.subscription = this.dealerService.getEventsObservable().subscribe((event) => {
            if (event.name === BaccaratEventType.CutCardDrawn) {
                this.handleCutCardDrawnEvent();
            } else if (event.name === BaccaratEventType.DealerAssistTableError) {
                this.handleDealerAssistTableError(event.data as DealerAssistTableErrorEvent);
            }
        });
    }

    ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    async onFlagGameClick(button: MatButton): Promise<void> {
        await this.onActionClick(button, FlagStateType.RoundFlagged);
    }

    async onFreeGameClick(button: MatButton): Promise<void> {
        await this.onActionClick(button, FlagStateType.RoundBetsFree);
    }

    async onCallSupervisorClick(button: MatButton): Promise<void> {
        await this.onActionClick(button, FlagStateType.SupervisorCalled);
    }

    async onCloseTableClick(button: MatButton): Promise<void> {
        await this.onActionClick(button, FlagStateType.TableClosingRequested);
    }

    async onEnableChatClick(button: MatButton): Promise<void> {
        await this.onActionClick(button, FlagStateType.ChatEnabled);
    }

    async onDisableChatClick(button: MatButton): Promise<void> {
        await this.onActionClick(button, FlagStateType.ChatEnabled, false);
    }

    async onChangeShoeClick(button: MatButton): Promise<void> {
        try {
            this.setButtonDisabledState(button, true);
            await this.dealerService.sendDealerActionRequest(DealerConsoleAction.ChangeShoe);
        } catch (error) {
            this.setButtonDisabledState(button, false);
            console.error(error);
            this.snackBar.open('Action failed', 'Close');
        }
    }

    private async onActionClick(button: MatButton, flag: FlagStateType, state = true): Promise<void> {
        try {
            this.setButtonDisabledState(button, true);
            await this.dealerService.sendSetFlagStateRequest(flag, state);
            if (flag === FlagStateType.ChatEnabled) {
                this.setButtonDisabledState(button, false);
            }
        } catch (error) {
            this.setButtonDisabledState(button, false);
            console.error(error);
            this.snackBar.open($localize`Action failed`, $localize`Close`);
        }
    }

    private setButtonDisabledState(button: MatButton, disabled: boolean): void {
        // eslint-disable-next-line no-param-reassign
        button.disabled = disabled;
    }

    private createTimeObservable(): Observable<string> {
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        return timer(0, 1000).pipe(map(() => format(new Date(), 'HH:mm:ss')));
    }

    private handleCutCardDrawnEvent(): void {
        this.snackBar.open('Cut card drawn', 'Close', { duration: 3000 });
    }

    private handleDealerAssistTableError(event: DealerAssistTableErrorEvent): void {
        console.error(event);
        this.snackBar.open(this.errorCodeToMessage(event.errorCode), $localize`Close`);
    }

    private errorCodeToMessage(errorCode: DealerAssistTableErrorCode): string {
        switch (errorCode) {
            case DealerAssistTableErrorCode.AuthenticationFailed:
                return $localize`Authentication failed`;
            case DealerAssistTableErrorCode.CroupierChangeInvalidState:
                return $localize`Croupier change invalid state`;
            case DealerAssistTableErrorCode.DuplicateCard:
                return $localize`Duplicate card`;
            case DealerAssistTableErrorCode.FlagFailed:
                return $localize`Flag failed`;
            case DealerAssistTableErrorCode.InvalidCard:
                return $localize`Invalid card`;
            case DealerAssistTableErrorCode.InvalidCroupierId:
                return $localize`Invalid croupier ID`;
            default:
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                console.error(`Unknown error code: ${errorCode}`);
                return errorCode;
        }
    }
}
